import React, { useEffect, useState } from 'react';
import { Navigate, useLocation } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';
import { getSubscriptionState, isAdmin } from '@/utils/subscription';
import { OncoAgentPaywall } from './OncoAgentPaywall';

export function OncoAgentAuthGate({ children }: { children: React.ReactNode }): React.ReactElement {
  const { user, isAuthenticated, loading } = useAuth();
  const location = useLocation();
  const [checking, setChecking] = useState(true);
  const [hasAccess, setHasAccess] = useState(false);
  const returnUrl = location.pathname + location.search;

  const check = async () => {
    if (!isAuthenticated || !user) {
      setHasAccess(false);
      setChecking(false);
      return;
    }
    if (isAdmin(user)) {
      setHasAccess(true);
      setChecking(false);
      return;
    }
    try {
      const state = await getSubscriptionState(user);
      setHasAccess(!!state.isActive);
    } catch {
      setHasAccess(false);
    } finally {
      setChecking(false);
    }
  };

  useEffect(() => {
    if (loading) return;
    check();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user, isAuthenticated, loading, location.pathname]);

  if (loading || checking) {
    return (
      <div className="min-h-screen flex items-center justify-center" style={{ background: '#f6f4ef' }}>
        <p style={{ color: '#6d6a63' }}>Checking your OncoAgent session…</p>
      </div>
    );
  }

  if (!isAuthenticated) {
    return <Navigate to={`/authentication?redirect=${encodeURIComponent(returnUrl)}`} replace />;
  }

  if (!hasAccess) {
    return <OncoAgentPaywall returnPath={returnUrl} onUnlocked={check} />;
  }

  return <>{children}</>;
}

export default OncoAgentAuthGate;
